import { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { IconGoLiveSidebar } from 'assets/svg/sprite'
import { AllIcons } from 'helpers/allIcons'

interface NewPostPopupDesktopProps {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    refButton: React.RefObject<HTMLElement>
}

export function NewPostPopupDesktop({
    isOpen,
    setIsOpen,
    refButton
}: NewPostPopupDesktopProps) {
    const ref = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const location = useLocation()

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node

            if (
                ref.current &&
                !ref.current.contains(target) &&
                refButton.current &&
                !refButton.current.contains(target)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener('click', handleClickOutside, true)

        return () => {
            document.removeEventListener('click', handleClickOutside, true)
        }
    }, [isOpen, refButton, setIsOpen])

    return (
        <Container
            ref={ref}
            $isOpen={isOpen}
            aria-hidden={!isOpen}
        >
            <Item
                to="/new/post/create"
                onClick={() => setIsOpen(false)}
            >
                <img
                    src={AllIcons.footer_newpost}
                    alt={t('post')}
                />
                {t('post')}
            </Item>

            <Item
                to={{ pathname: '/messages/new' }}
                state={{ activePage: 'newmessage' }}
                onClick={() => setIsOpen(false)}
            >
                <img
                    src={AllIcons.footer_newmessage}
                    alt={t('message')}
                />
                {t('message')}
            </Item>

            <Item
                to="/new/story"
                onClick={() => setIsOpen(false)}
            >
                <img
                    src={AllIcons.footer_newstory}
                    alt={t('story')}
                />
                {t('story')}
            </Item>

            <Item
                to="/new/reel"
                state={{
                    backgroundPath: location.pathname
                }}
                onClick={() => setIsOpen(false)}
            >
                <img
                    src={AllIcons.footer_newstory}
                    alt={t('reel')}
                />
                {t('reel')}
            </Item>
<Item
    to="/live"
    onClick={(e) => {
        e.preventDefault()
    }}
    className="live-item disabled"
>
    <IconGoLiveSidebar />
    Go Live
</Item>
        </Container>
    )
}

const Container = styled.div<{
    $isOpen: boolean
}>`
    background: white;
    border-radius: 2.5rem;
    padding: 3rem;
    display: flex;
    flex-direction: column;
    gap: 3rem;
    min-width: max-content;
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.12);

    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) =>
        $isOpen ? 'visible' : 'hidden'};
    pointer-events: ${({ $isOpen }) =>
        $isOpen ? 'auto' : 'none'};

    transition:
        opacity 120ms ease,
        visibility 120ms ease;
`

const Item = styled(Link)`
    display: flex;
    align-items: center;
    white-space: nowrap;
    text-decoration: none;
    font-family: 'Montserrat', sans-serif;
    font-style: normal;
    font-variation-settings: 'wght' 650;
    letter-spacing: 0.02em;
    font-size: 1.4rem;
    line-height: 1.5rem;
    color: #000;

    img,
    svg {
        margin-right: 1.2rem;
        flex-shrink: 0;
    }

    img {
        filter: brightness(0);
    }

    svg {
        color: #000;
    }
&.disabled,
&.disabled:hover,
&.disabled:active,
&.disabled:focus {
    color: #757576;
    opacity: 0.4;
    cursor: not-allowed;

    svg {
        filter: grayscale(1) brightness(0.55);
    }
}


    &:not(.live-item) svg path {
        fill: currentColor;
        stroke: currentColor;
    }

    &.live-item svg {
        padding: 1px 0;
    }
`